import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ChooseIcon from '@/common/components/forms/choose-icon';
import formStyle from '@/common/components/forms/forms.module.scss';
import InputField from '@/common/components/forms/input-field';
import SelectField from '@/common/components/forms/select-field';
import SwitchField from '@/common/components/forms/switch-field';
import { BUDGET_ICONS, CURRENCIES } from '@/constants';
import { useEffect, useMemo, useRef, useState } from 'react';
function useDebouncedChanged(value, onChanged, delayMs) {
    const onChangedRef = useRef(onChanged);
    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        onChangedRef.current = onChanged;
    }, [onChanged]);
    useEffect(() => {
        if (!onChangedRef.current) {
            return;
        }
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        const timerId = window.setTimeout(() => {
            onChangedRef.current?.(value);
        }, delayMs);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [value, delayMs]);
}
export default function CreateBudgetForm({ date, onChanged, changeDebounceMs = 250, }) {
    const [name, setName] = useState('');
    const [currency, setCurrency] = useState('RUB');
    const [balance, setBalance] = useState('');
    const [weekStart, setWeekStart] = useState(1);
    const [startDate, setStartDate] = useState(date);
    const [icon, setIcon] = useState('💼');
    const [savingsEnabled, setSavingsEnabled] = useState(false);
    const [showCurrentBalance, setShowCurrentBalance] = useState(true);
    useEffect(() => {
        setStartDate(date);
    }, [date]);
    const createData = useMemo(() => !name.trim()
        ? null
        : {
            name: name.trim(),
            currency,
            startBalance: parseFloat(balance) || 0,
            weekStart,
            startDate,
            icon,
            savingsEnabled,
            showCurrentBalance,
        }, [name, currency, balance, weekStart, startDate, icon, savingsEnabled, showCurrentBalance]);
    useDebouncedChanged(createData, onChanged, changeDebounceMs);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: formStyle['form-row'], style: { alignItems: 'flex-start' }, children: [_jsx("div", { "data-tut": "create-icon-name", children: _jsx(ChooseIcon, { iconSet: BUDGET_ICONS, icon: icon, onIconSelected: setIcon }) }), _jsxs("div", { "data-tut": "create-settings", children: [_jsx(InputField, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", value: name, onSetValue: setName, autoFocus: true }), _jsx(SelectField, { label: "\u0412\u0430\u043B\u044E\u0442\u0430", options: CURRENCIES.map(({ code, sym }) => ({ id: code, label: `${sym} ${code}` })), value: currency, onSetValue: setCurrency }), _jsx(SelectField, { label: "\u041D\u0430\u0447\u0430\u043B\u043E \u043D\u0435\u0434\u0435\u043B\u0438", options: [
                                    { id: 1, label: 'Понедельник' },
                                    { id: 0, label: 'Воскресенье' },
                                ], value: weekStart, onSetValue: (value) => setWeekStart(Number(value)) })] })] }), _jsxs("div", { className: formStyle['form-row'], "data-tut": "create-balance", children: [_jsx(InputField, { label: "\u0421\u0442\u0430\u0440\u0442\u043E\u0432\u044B\u0439 \u0431\u0430\u043B\u0430\u043D\u0441", inputType: "amount", description: "\u0421\u0442\u0430\u043D\u0435\u0442 \u043F\u0435\u0440\u0432\u043E\u0439 \u0442\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u0435\u0439", value: balance, onSetValue: setBalance }), _jsx(InputField, { label: "\u0414\u0430\u0442\u0430 \u043D\u0430\u0447\u0430\u043B\u0430", inputType: "date", value: startDate, onSetValue: setStartDate })] }), _jsx("div", { "data-tut": "create-savings", children: _jsx(SwitchField, { isOn: savingsEnabled, onToggle: setSavingsEnabled, label: "\u041D\u0430\u043A\u043E\u043F\u043B\u0435\u043D\u0438\u044F \u0432\u043A\u043B\u044E\u0447\u0435\u043D\u044B", description: "\u0414\u043E\u0431\u0430\u0432\u043B\u044F\u0435\u0442 \u0442\u0440\u0435\u0442\u0438\u0439 \u0442\u0438\u043F \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0439 \u0434\u043B\u044F \u0446\u0435\u043B\u0435\u0439 \u0438 \u0441\u0431\u0435\u0440\u0435\u0436\u0435\u043D\u0438\u0439", style: {
                        padding: '10px 0 8px',
                        borderTop: '1px solid var(--border-light)',
                        marginTop: 10,
                    } }) }), _jsx(SwitchField, { label: "\u0411\u0430\u043B\u0430\u043D\u0441 \u043F\u043E \u0442\u0435\u043A\u0443\u0449\u0435\u0439 \u0434\u0430\u0442\u0435", description: "\u041F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0442\u044C \u0444\u0430\u043A\u0442\u0438\u0447\u0435\u0441\u043A\u0438\u0439 \u0431\u0430\u043B\u0430\u043D\u0441 \u043D\u0430 \u0441\u0435\u0433\u043E\u0434\u043D\u044F. \u0415\u0441\u043B\u0438 \u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D\u043E \u2014 \u043F\u043E \u0432\u0441\u0435\u043C \u0438\u043C\u0435\u044E\u0449\u0438\u043C\u0441\u044F \u0434\u0430\u043D\u043D\u044B\u043C", isOn: showCurrentBalance, onToggle: setShowCurrentBalance, style: {
                    padding: '10px 0 8px',
                    borderTop: '1px solid var(--border-light)',
                    marginTop: 10,
                } })] }));
}
